import { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const NotificationBell = () => {
  const { refresh } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications");
      if (seen.current) {
        const fresh = d.items.filter((n) => !n.read && !seen.current.has(n.notif_id));
        fresh.forEach((n) => toast(n.title, { description: n.body }));
        if (fresh.length) refresh();
      }
      seen.current = new Set(d.items.map((n) => n.notif_id));
      setData(d);
    } catch { /* ignore */ }
  }, [refresh]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const onOpen = (open) => {
    if (open && data.unread) {
      api.post("/notifications/read-all").then(() => setData((d) => ({ ...d, unread: 0 })));
    }
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-full border border-[#212638] p-2 text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">
          <Bell size={16} />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-[#F7931A] px-1 font-mono text-[10px] font-bold text-[#08090E]">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-[#212638] bg-[#151824] p-0 text-[#F8FAFC]" data-testid="notification-list">
        <p className="border-b border-[#212638] px-4 py-3 text-sm font-semibold">Notifications</p>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 ? <p className="px-4 py-6 text-center text-xs text-[#64748B]">No notifications yet.</p> :
            data.items.map((n) => (
              <div key={n.notif_id} className={`border-b border-[#212638] px-4 py-3 ${n.read ? "" : "bg-[#F7931A]/5"}`} data-testid={`notification-${n.notif_id}`}>
                <p className="text-sm font-semibold">{n.title}</p>
                <p className="mt-0.5 text-xs text-[#94A3B8]">{n.body}</p>
                <p className="mt-1 text-[10px] text-[#64748B]">{new Date(n.created_at).toLocaleString()}</p>
              </div>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
